import { achievementById, type QuestEvent, type Rarity, type UnlockKind } from '@anidex/shared/economy';
import i18n from './index';

function text(key: string, fallback: string): string {
  const value = i18n.t(key, { defaultValue: fallback });
  return value || fallback;
}

export function boosterName(id: string, fallback: string): string {
  return text(`catalog:boosters.${id}.name`, fallback);
}

export function boosterShortName(id: string, fallback: string): string {
  return text(`catalog:boosters.${id}.short`, fallback);
}

export function boosterDescription(id: string, fallback: string): string {
  return text(`catalog:boosters.${id}.description`, fallback);
}

export function rarityName(rarity: Rarity | string, fallback?: string): string {
  return text(`catalog:rarities.${rarity}`, fallback ?? rarity);
}

export function roleName(role: string): string {
  return text(`catalog:roles.${role}`, role);
}


export function achievementTitle(id: string, fallback?: string): string {
  return text(`catalog:achievements.${id}.title`, achievementById(id)?.title ?? (fallback || id));
}

export function achievementTierTitle(id: string, tier: number, fallback?: string): string {
  return i18n.t('play:achievements.tierTitle', {
    title: achievementTitle(id, fallback),
    tier,
    defaultValue: `${achievementTitle(id, fallback)} (tier ${tier})`,
  });
}


export function achievementDescription(id: string, target: number): string {
  const def = achievementById(id);
  const formatted = new Intl.NumberFormat(i18n.language).format(target);
  const value = i18n.t(`catalog:achievements.${id}.description`, {
    count: target,
    formatted,
    defaultValue: def ? def.description.replaceAll('{n}', formatted) : '',
  });
  return value || id;
}

export function achievementCategory(category: string, fallback: string): string {
  return text(`catalog:categories.${category}`, fallback);
}

export function unlockLabel(kind: string, key: string, fallback: string): string {
  return text(`catalog:unlocks.${kind}.${key}`, fallback);
}

export function upgradeUnlock(level: number, fallback: string): string {
  return text(`catalog:upgrades.${level}`, fallback);
}

export function questTitle(event: QuestEvent, fallback: string, variant = 0): string {
  const varied = text(`catalog:quests.${event}.v${variant}.title`, '');
  if (varied) return varied;
  return text(`catalog:quests.${event}.title`, fallback);
}

export function questDescription(event: QuestEvent, target: number, fallback: string, variant = 0): string {
  const varied = i18n.t(`catalog:quests.${event}.v${variant}.description`, { count: target, defaultValue: '' });
  if (varied) return varied;
  if (event === 'collection_viewed') return text('catalog:quests.collection_viewed.description', fallback);
  const value = i18n.t(`catalog:quests.${event}.description`, { count: target, defaultValue: fallback });
  return value || fallback;
}

export function unlockKindLabel(kind: UnlockKind): string {
  return i18n.t(`unlocks.kinds.${kind}`);
}
